/**
 * viewport.ts (M95) — ONE subscription point for "the visible screen size
 * changed", replacing every bare `window.addEventListener('resize', …)`.
 *
 * Why the bare resize event is not enough on phones (the owner's "sometimes
 * the game doesn't fill the screen — black empty space across the bottom"):
 *  - iOS fires `resize` DURING an orientation flip while innerWidth/Height
 *    can still report the OLD orientation; if no later event arrives, every
 *    canvas sized in that handler keeps the stale size forever and the page
 *    background shows as a dead band.
 *  - Browser toolbar collapse/expand on some UAs only announces itself
 *    reliably through `visualViewport`.
 *
 * So a subscriber's callback re-fires on: window resize, orientationchange,
 * visualViewport resize, AND a ResizeObserver on <html> (the one signal that
 * follows layout truth with no event-timing race). After any trigger the
 * callback also re-runs on a short settle ramp so the LAST measurement is
 * always post-layout. Callbacks must therefore be idempotent re-measures —
 * every existing resize handler in the game already is (read live size,
 * apply it).
 */

export type ViewportUnsubscribe = () => void;

/** Re-measure ramp after a trigger: covers iOS's stale-then-settled window. */
const SETTLE_DELAYS_MS = [60, 180, 420];

export function subscribeViewport(cb: () => void): ViewportUnsubscribe {
  let timers: number[] = [];
  let disposed = false;

  const fire = (): void => {
    if (!disposed) cb();
  };
  const trigger = (): void => {
    fire();
    for (const t of timers) window.clearTimeout(t);
    timers = SETTLE_DELAYS_MS.map((ms) => window.setTimeout(fire, ms));
  };

  window.addEventListener('resize', trigger);
  window.addEventListener('orientationchange', trigger);
  const vv = window.visualViewport;
  vv?.addEventListener('resize', trigger);

  // <html> has height:100% under a position:fixed body, so its box IS the
  // layout viewport — the observer fires post-layout on any change, even ones
  // that never produced a window event. (Its initial async fire is a harmless
  // idempotent re-measure.)
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(fire);
    ro.observe(document.documentElement);
  }

  return () => {
    disposed = true;
    window.removeEventListener('resize', trigger);
    window.removeEventListener('orientationchange', trigger);
    vv?.removeEventListener('resize', trigger);
    ro?.disconnect();
    for (const t of timers) window.clearTimeout(t);
    timers = [];
  };
}
